import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Grid,
  GridItem,
  Heading,
  Icon,
  SimpleGrid,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import {
  MdAgriculture,
  MdArrowForward,
  MdAssignment,
  MdCheckCircle,
  MdPeople,
  MdStorefront,
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AppCard, DashboardLoader } from '../../../components/ui';
import { getDashboardCounts } from '../../../repositories/dashboard.repository';

/** Chart palette — hex values because Recharts renders raw SVG. */
const CHART = {
  amber: '#F9B101',
  amberSoft: '#FBD66A',
  success: '#2E7D32',
  successSoft: '#66BB6A',
  info: '#1B4D3E',
  infoSoft: '#3E7F6B',
  warning: '#B86E00',
  danger: '#C62828',
  muted: '#9C9992',
};

/** Shared plot height so the pipeline and equipment panels line up. */
const CHART_H = 196;

function pct(part, whole) {
  const p = Number(part) || 0;
  const w = Number(whole) || 0;
  if (w <= 0) return 0;
  return Math.round((p / w) * 100);
}

function ChartTooltip({ active, payload, label, total }) {
  const bg = useColorModeValue('#FFFFFF', '#242220');
  const border = useColorModeValue('#E2DED2', '#3D3A35');
  const text = useColorModeValue('#141414', '#FAF9F6');
  const sub = useColorModeValue('#5C574C', '#A8A49A');

  if (!active || !payload?.length) return null;
  const row = payload[0];
  const name = row?.payload?.name || label;
  const value = Number(row?.value) || 0;

  return (
    <Box
      bg={bg}
      borderWidth="1px"
      borderColor={border}
      borderRadius="md"
      px={3}
      py={2}
      boxShadow="lg"
      minW="140px"
    >
      <Text fontSize="12px" color={sub} mb={0.5}>
        {name}
      </Text>
      <Text fontSize="16px" fontWeight="700" color={text} fontVariantNumeric="tabular-nums">
        {value}
        {total ? (
          <Text as="span" fontSize="12px" fontWeight="500" color={sub} ml={1.5}>
            {pct(value, total)}% of {total}
          </Text>
        ) : null}
      </Text>
    </Box>
  );
}

function PanelHeader({ title, subtitle, right }) {
  return (
    <Flex justify="space-between" align="flex-start" gap={3} mb={4}>
      <Box minW={0}>
        <Text fontWeight="700" fontSize="15px" color="text.primary">
          {title}
        </Text>
        {subtitle ? (
          <Text fontSize="13px" color="text.secondary" mt={0.5}>
            {subtitle}
          </Text>
        ) : null}
      </Box>
      {right}
    </Flex>
  );
}

function EmptyChart({ label, h = 240 }) {
  return (
    <Flex
      align="center"
      justify="center"
      h={`${h}px`}
      borderRadius="md"
      bg="bg.input"
      borderWidth="1px"
      borderStyle="dashed"
      borderColor="border.subtle"
    >
      <Text fontSize="13px" color="text.secondary">
        {label}
      </Text>
    </Flex>
  );
}

function KpiTile({ icon, label, value, hint, share, shareLabel, color, onClick }) {
  const trackColor = useColorModeValue('#F3F1EC', '#2A2825');
  const data = [
    { name: shareLabel, value: Math.max(0, Number(share) || 0) },
    { name: 'Rest', value: Math.max(0, 100 - (Number(share) || 0)) },
  ];

  return (
    <AppCard
      p={4}
      h="100%"
      cursor={onClick ? 'pointer' : 'default'}
      onClick={onClick}
      transition="border-color 0.15s ease"
      _hover={onClick ? { borderColor: 'utkarsh.amber' } : undefined}
    >
      <Flex align="center" justify="space-between" gap={3}>
        <Box minW={0}>
          <Flex align="center" gap={2} mb={2}>
            <Icon as={icon} boxSize="16px" color="text.secondary" />
            <Text
              fontSize="11px"
              fontWeight="700"
              letterSpacing="0.06em"
              textTransform="uppercase"
              color="text.secondary"
              noOfLines={1}
            >
              {label}
            </Text>
          </Flex>
          <Text
            fontSize="30px"
            fontWeight="700"
            lineHeight="1.05"
            fontVariantNumeric="tabular-nums"
          >
            {value ?? 0}
          </Text>
          <Text fontSize="12px" color="text.secondary" mt={1.5} noOfLines={2}>
            {hint}
          </Text>
        </Box>

        <Box position="relative" w="68px" h="68px" flexShrink={0}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                innerRadius={24}
                outerRadius={33}
                startAngle={90}
                endAngle={-270}
                stroke="none"
                isAnimationActive={false}
              >
                <Cell fill={color} />
                <Cell fill={trackColor} />
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <Flex
            position="absolute"
            inset={0}
            align="center"
            justify="center"
            pointerEvents="none"
          >
            <Text fontSize="12px" fontWeight="700" fontVariantNumeric="tabular-nums">
              {Math.max(0, Number(share) || 0)}%
            </Text>
          </Flex>
        </Box>
      </Flex>
    </AppCard>
  );
}

export default function Dashboard() {
  const toast = useToast();
  const navigate = useNavigate();
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);

  const axisColor = useColorModeValue('#5C574C', '#A8A49A');
  const gridStroke = useColorModeValue('#E2DED2', '#3D3A35');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getDashboardCounts();
        if (!cancelled) setCounts(data);
      } catch (err) {
        if (!cancelled) {
          toast({
            title: err?.message || 'Failed to load dashboard',
            status: 'error',
            position: 'top-right',
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [toast]);

  const v = counts?.villagers || {};
  const vd = counts?.vendors || {};
  const eq = counts?.equipment || {};
  const ind = counts?.indents || {};

  const pipelineData = useMemo(() => {
    const i = counts?.indents || {};
    return [
      { name: 'Pending', value: Number(i.pending) || 0, fill: CHART.warning },
      { name: 'Recommended', value: Number(i.recommended) || 0, fill: CHART.info },
      { name: 'Assigned', value: Number(i.assigned) || 0, fill: CHART.amber },
      { name: 'Released', value: Number(i.released) || 0, fill: CHART.success },
      { name: 'Cancelled', value: Number(i.cancelled) || 0, fill: CHART.muted },
    ];
  }, [counts]);
  const pipelineTotal = pipelineData.reduce((s, r) => s + r.value, 0);
  const pipelineMax = Math.max(1, ...pipelineData.map((r) => r.value));

  const fleetData = useMemo(() => {
    const e = counts?.equipment || {};
    return [
      { name: 'Available', value: Number(e.available) || 0, fill: CHART.success },
      { name: 'Deployed', value: Number(e.deployed) || 0, fill: CHART.amber },
      { name: 'Pending KYC', value: Number(e.pendingKyc) || 0, fill: CHART.warning },
    ];
  }, [counts]);
  const fleetTotal = fleetData.reduce((s, r) => s + r.value, 0);

  const peopleData = useMemo(() => {
    const vl = counts?.villagers || {};
    const vn = counts?.vendors || {};
    return [
      {
        name: 'Villagers',
        Verified: Number(vl.verified) || 0,
        Pending: Number(vl.pendingKyc) || 0,
        Blocked: 0,
      },
      {
        name: 'Vendors',
        Verified: Number(vn.verified) || 0,
        Pending: Number(vn.pending) || 0,
        Blocked: Number(vn.blocked) || 0,
      },
    ];
  }, [counts]);
  const peopleTotal =
    (Number(v.total) || 0) + (Number(vd.total) || 0);

  const attention = useMemo(() => {
    const i = counts?.indents || {};
    const e = counts?.equipment || {};
    const vl = counts?.villagers || {};
    const vn = counts?.vendors || {};
    return [
      {
        key: 'indent-pending',
        label: 'Open requests',
        value: Number(i.pending) || 0,
        color: CHART.warning,
        to: '/admin/equipments/indents',
      },
      {
        key: 'eq-kyc',
        label: 'Equipment KYC pending',
        value: Number(e.pendingKyc) || 0,
        color: CHART.amber,
        to: '/admin/equipments/fleet',
      },
      {
        key: 'villager-kyc',
        label: 'Villager KYC pending',
        value: Number(vl.pendingKyc) || 0,
        color: CHART.info,
        to: '/admin/equipments/villagers',
      },
      {
        key: 'vendor-pending',
        label: 'Vendor reviews pending',
        value: Number(vn.pending) || 0,
        color: CHART.danger,
        to: '/admin/equipments/vendors',
      },
    ].filter((row) => row.value > 0);
  }, [counts]);

  if (loading) {
    return <DashboardLoader cards={4} />;
  }

  return (
    <Box maxW="1440px">
      <Box mb={5}>
        <Heading size="lg" letterSpacing="-0.02em">
          Dashboard
        </Heading>
        <Text color="text.secondary" mt={1} fontSize="14px">
          Operations snapshot for your scope — requests, fleet readiness, and onboarding health.
        </Text>
      </Box>

      {/* Zone 1 — primary KPIs with share rings */}
      <SimpleGrid columns={{ base: 1, sm: 2, xl: 4 }} spacing={3} mb={3}>
        <KpiTile
          icon={MdAssignment}
          label="Requests"
          value={ind.total ?? 0}
          hint={`${ind.pending ?? 0} pending · ${ind.assigned ?? 0} live · ${ind.released ?? 0} released`}
          share={pct((ind.assigned || 0) + (ind.released || 0), ind.total)}
          shareLabel="Actioned"
          color={CHART.amber}
          onClick={() => navigate('/admin/equipments/indents')}
        />
        <KpiTile
          icon={MdAgriculture}
          label="Equipment"
          value={eq.total ?? 0}
          hint={`${eq.available ?? 0} available · ${eq.deployed ?? 0} deployed`}
          share={pct(eq.available, eq.total)}
          shareLabel="Available"
          color={CHART.success}
          onClick={() => navigate('/admin/equipments/fleet')}
        />
        <KpiTile
          icon={MdPeople}
          label="Villagers"
          value={v.total ?? 0}
          hint={`${v.verified ?? 0} verified · ${v.pendingKyc ?? 0} pending KYC`}
          share={pct(v.verified, v.total)}
          shareLabel="Verified"
          color={CHART.info}
          onClick={() => navigate('/admin/equipments/villagers')}
        />
        <KpiTile
          icon={MdStorefront}
          label="Vendors"
          value={vd.total ?? 0}
          hint={`${vd.verified ?? 0} verified · ${vd.pending ?? 0} pending · ${vd.blocked ?? 0} blocked`}
          share={pct(vd.verified, vd.total)}
          shareLabel="Verified"
          color={CHART.amberSoft}
          onClick={() => navigate('/admin/equipments/vendors')}
        />
      </SimpleGrid>

      {/* Zone 2 — actionable queue */}
      {attention.length ? (
        <AppCard p={0} overflow="hidden" mb={3}>
          <Flex
            px={4}
            py={2.5}
            borderBottomWidth="1px"
            borderColor="border.subtle"
            bg="bg.input"
            align="center"
            justify="space-between"
          >
            <Text
              fontSize="11px"
              fontWeight="700"
              letterSpacing="0.06em"
              textTransform="uppercase"
              color="text.secondary"
            >
              Needs attention
            </Text>
          </Flex>
          <SimpleGrid columns={{ base: 1, sm: 2, lg: attention.length }} spacing={0}>
            {attention.map((item, idx) => (
              <Flex
                key={item.key}
                as="button"
                type="button"
                onClick={() => navigate(item.to)}
                textAlign="left"
                px={4}
                py={3.5}
                align="center"
                gap={3}
                borderRightWidth={{ lg: idx < attention.length - 1 ? '1px' : 0 }}
                borderBottomWidth={{ base: '1px', lg: 0 }}
                borderColor="border.subtle"
                _hover={{ bg: 'bg.input' }}
                sx={{ '& > *': { pointerEvents: 'none' } }}
              >
                <Box w="8px" h="8px" borderRadius="full" bg={item.color} flexShrink={0} />
                <Box flex="1" minW={0}>
                  <Text fontSize="12px" color="text.secondary" noOfLines={1}>
                    {item.label}
                  </Text>
                  <Text
                    fontSize="20px"
                    fontWeight="700"
                    lineHeight="1.15"
                    fontVariantNumeric="tabular-nums"
                  >
                    {item.value}
                  </Text>
                </Box>
                <Icon as={MdArrowForward} boxSize="16px" color="text.secondary" />
              </Flex>
            ))}
          </SimpleGrid>
        </AppCard>
      ) : (
        <AppCard p={4} mb={3}>
          <Flex align="center" gap={3}>
            <Icon as={MdCheckCircle} boxSize={5} color="status.success" />
            <Box>
              <Text fontWeight="600" fontSize="14px">
                Operations look clear
              </Text>
              <Text fontSize="13px" color="text.secondary">
                No pending KYC, vendor reviews, or open requests in your scope.
              </Text>
            </Box>
          </Flex>
        </AppCard>
      )}

      {/* Zone 3 — charts */}
      <Grid templateColumns={{ base: '1fr', xl: '1.3fr 1fr' }} gap={3} mb={3}>
        <GridItem>
          <AppCard p={{ base: 4, xl: 5 }} h="100%">
            <PanelHeader
              title="Request pipeline"
              subtitle="Where indents sit today, from raise to release"
              right={
                <Box textAlign="right" flexShrink={0}>
                  <Text fontSize="11px" fontWeight="700" textTransform="uppercase" color="text.secondary">
                    Total
                  </Text>
                  <Text fontSize="20px" fontWeight="700" fontVariantNumeric="tabular-nums" lineHeight="1.2">
                    {pipelineTotal}
                  </Text>
                </Box>
              }
            />
            {pipelineTotal === 0 ? (
              <EmptyChart label="No requests in scope yet" h={CHART_H} />
            ) : (
              <Box h={`${CHART_H}px`}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={pipelineData}
                    layout="vertical"
                    margin={{ top: 0, right: 36, bottom: 0, left: 0 }}
                    barCategoryGap="14%"
                  >
                    <XAxis type="number" domain={[0, pipelineMax]} hide />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={96}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: axisColor, fontSize: 12 }}
                    />
                    <Tooltip
                      cursor={{ fill: gridStroke, opacity: 0.25 }}
                      content={<ChartTooltip total={pipelineTotal} />}
                    />
                    <Bar
                      dataKey="value"
                      radius={[0, 6, 6, 0]}
                      maxBarSize={30}
                      background={{ fill: gridStroke, opacity: 0.28, radius: 6 }}
                    >
                      {pipelineData.map((row) => (
                        <Cell key={row.name} fill={row.fill} />
                      ))}
                      <LabelList
                        dataKey="value"
                        position="right"
                        style={{ fill: axisColor, fontSize: 12, fontWeight: 700 }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </AppCard>
        </GridItem>

        <GridItem>
          <AppCard p={{ base: 4, xl: 5 }} h="100%">
            <PanelHeader
              title="Equipment readiness"
              subtitle="Fleet assets available vs on work vs KYC hold"
            />
            {fleetTotal === 0 ? (
              <EmptyChart label="No equipment in scope yet" h={CHART_H} />
            ) : (
              <Flex h={`${CHART_H}px`} align="center" gap={5}>
                <Box position="relative" w={`${CHART_H}px`} h={`${CHART_H}px`} flexShrink={0}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                      <Pie
                        data={fleetData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius="64%"
                        outerRadius="94%"
                        paddingAngle={2}
                        stroke="none"
                      >
                        {fleetData.map((row) => (
                          <Cell key={row.name} fill={row.fill} />
                        ))}
                      </Pie>
                      <Tooltip content={<ChartTooltip total={fleetTotal} />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <Flex
                    position="absolute"
                    inset={0}
                    align="center"
                    justify="center"
                    direction="column"
                    pointerEvents="none"
                  >
                    <Text
                      fontSize="28px"
                      fontWeight="700"
                      lineHeight="1"
                      fontVariantNumeric="tabular-nums"
                    >
                      {fleetTotal}
                    </Text>
                    <Text
                      fontSize="10px"
                      color="text.secondary"
                      textTransform="uppercase"
                      letterSpacing="0.08em"
                      mt={1}
                    >
                      Assets
                    </Text>
                  </Flex>
                </Box>

                <Box flex="1" minW={0}>
                  {fleetData.map((row, idx) => (
                    <Flex
                      key={row.name}
                      align="center"
                      gap={3}
                      py={2.5}
                      borderTopWidth={idx === 0 ? 0 : '1px'}
                      borderColor="border.subtle"
                    >
                      <Box
                        w="10px"
                        h="10px"
                        borderRadius="sm"
                        bg={row.fill}
                        flexShrink={0}
                      />
                      <Text flex="1" fontSize="13px" noOfLines={1}>
                        {row.name}
                      </Text>
                      <Text
                        fontSize="15px"
                        fontWeight="700"
                        fontVariantNumeric="tabular-nums"
                      >
                        {row.value}
                      </Text>
                      <Text
                        fontSize="12px"
                        color="text.secondary"
                        fontVariantNumeric="tabular-nums"
                        minW="38px"
                        textAlign="right"
                      >
                        {pct(row.value, fleetTotal)}%
                      </Text>
                    </Flex>
                  ))}
                </Box>
              </Flex>
            )}
          </AppCard>
        </GridItem>
      </Grid>

      <AppCard p={{ base: 4, xl: 5 }}>
        <PanelHeader
          title="Onboarding health"
          subtitle="Verification mix across villagers and vendors"
          right={
            <Box textAlign="right" flexShrink={0}>
              <Text fontSize="11px" fontWeight="700" textTransform="uppercase" color="text.secondary">
                People
              </Text>
              <Text fontSize="20px" fontWeight="700" fontVariantNumeric="tabular-nums" lineHeight="1.2">
                {peopleTotal}
              </Text>
            </Box>
          }
        />
        {peopleTotal === 0 ? (
          <EmptyChart label="No villagers or vendors in scope yet" h={220} />
        ) : (
          <Box h="240px">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={peopleData}
                margin={{ top: 8, right: 8, bottom: 4, left: 0 }}
                barGap={6}
                barCategoryGap="28%"
              >
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: axisColor, fontSize: 12, fontWeight: 600 }}
                />
                <YAxis
                  allowDecimals={false}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: axisColor, fontSize: 12 }}
                  width={32}
                />
                <Tooltip cursor={{ fill: gridStroke, opacity: 0.25 }} />
                <Legend
                  verticalAlign="top"
                  height={28}
                  iconType="circle"
                  iconSize={9}
                  formatter={(value) => (
                    <span style={{ color: axisColor, fontSize: 12 }}>{value}</span>
                  )}
                />
                <Bar dataKey="Verified" fill={CHART.success} radius={[6, 6, 0, 0]} maxBarSize={44} />
                <Bar dataKey="Pending" fill={CHART.amber} radius={[6, 6, 0, 0]} maxBarSize={44} />
                <Bar dataKey="Blocked" fill={CHART.danger} radius={[6, 6, 0, 0]} maxBarSize={44} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </AppCard>
    </Box>
  );
}
